/**
 * RemovableChip Component
 * Created: 08-10-2025 11:27:08
 * Purpose: Chip component with removable X icon for active filters display
 * 
 * Features:
 * - Small size with rounded pill shape
 * - X icon on hover for removal
 * - Smooth fade-in animation
 * - Color-coded by filter type
 * - Accessible with proper ARIA labels
 * 
 * @example
 * <RemovableChip
 *   label="Building A"
 *   type="building"
 *   onRemove={() => handleRemove('building-a')}
 * />
 */

import React, { useState } from 'react';
import { Text, ActionIcon, Box } from '@mantine/core';
import { IconX } from '@tabler/icons-react';

interface RemovableChipProps {
  /**
   * Display label for the chip
   */
  label: string;
  
  /**
   * Filter type for color coding
   */
  type?: 'building' | 'floor' | 'room' | 'sessionTime' | 'user' | 'status' | 'date' | 'vacancies';
  
  /**
   * Callback when remove icon clicked
   */
  onRemove: () => void;
  
  /**
   * Custom color override
   */
  color?: string;
  
  /**
   * Size variant
   * @default 'sm'
   */
  size?: 'xs' | 'sm' | 'md';
}

/**
 * Get color based on filter type
 */
const getColorByType = (type?: string): string => {
  const colorMap: Record<string, string> = {
    building: '#3B82F6', // Blue
    floor: '#8B5CF6',    // Purple
    room: '#10B981',     // Green
    sessionTime: '#F59E0B', // Orange
    user: '#EC4899',     // Pink
    status: '#6366F1',   // Indigo
    date: '#14B8A6',     // Teal
    vacancies: '#84CC16', // Lime
  };
  
  return type ? colorMap[type] || '#228BE6' : '#228BE6';
};

export const RemovableChip: React.FC<RemovableChipProps> = ({
  label,
  type,
  onRemove,
  color,
  size = 'sm',
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  const chipColor = color || getColorByType(type);
  
  const sizeStyles = {
    xs: { height: '24px', fontSize: '11px', padding: '0 8px' },
    sm: { height: '28px', fontSize: '12px', padding: '0 10px' },
    md: { height: '32px', fontSize: '13px', padding: '0 12px' },
  };
  
  return (
    <Box
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        backgroundColor: `${chipColor}15`, // 15% opacity
        border: `1px solid ${chipColor}40`,
        borderRadius: '9999px', // Full pill shape
        ...sizeStyles[size],
        transition: 'all 200ms ease',
        cursor: 'pointer',
        animation: 'fadeIn 300ms ease-out',
        
        ...(isHovered && {
          backgroundColor: `${chipColor}25`,
          borderColor: `${chipColor}60`,
          transform: 'scale(1.05)',
        }),
      }}
      role="button"
      aria-label={`Remove ${label} filter`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onRemove();
        }
      }}
    >
      <Text
        size={size}
        fw={500}
        style={{
          color: chipColor,
          lineHeight: 1,
        }}
      >
        {label}
      </Text>
      
      <ActionIcon
        size={size === 'xs' ? 14 : size === 'sm' ? 16 : 18}
        variant="transparent"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        style={{
          color: chipColor,
          transition: 'transform 150ms ease',
          
          '&:hover': {
            transform: 'rotate(90deg)',
          },
        }}
        aria-label={`Remove ${label}`}
      >
        <IconX size={size === 'xs' ? 10 : size === 'sm' ? 12 : 14} />
      </ActionIcon>
    </Box>
  );
};

export default RemovableChip;

